import * as React from 'react';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { Input, Paper } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Avatar } from '@boringer-avatars/react';
import { AvatarContainer, Frame12841 } from './style';
import styled from 'styled-components';

const DropdownIcon = styled(ExpandMoreIcon)`
  color: #6B7280;
  font-size: 24px;
`;

const getDisplayText = (val) => {
  if (val == null) return 'Select';
  if (typeof val === 'string') return val;
  const t = val?.title ?? val?.label ?? val?.name ?? val?.subjectName ?? val?.value;
  if (t == null) return 'Select';
  if (typeof t === 'string') return t;
  const u = t?.label ?? t?.title ?? t?.name ?? t?.value ?? '';
  return typeof u === 'string' ? u : 'Select';
};

// Custom Paper component with white background
const WhitePaper = styled(Paper)`
  background-color: #FFFFFF !important;
  background: #FFFFFF !important;

  & .MuiList-root {
    background-color: #FFFFFF !important;
    background: #FFFFFF !important;
    padding: 0 !important;
  }

  & .MuiMenuItem-root {
    background-color: #FFFFFF !important;
    background: #FFFFFF !important;

    &:hover {
      background-color: #F3F4F6 !important;
    }

    &.Mui-selected {
      background-color: #EDE9FE !important;

      &:hover {
        background-color: #E5DEFF !important;
      }
    }
  }
`;

export default function StyledDropDown({
  menuItems,
  showAvatars = false,
  search = false,
  group = false,
  independent = false,
  showImage = false,
  selectedIndex,
  fullWidth = false,
  onItemSelected,
  width = 120,
  borderRadius,
  menuZIndex,
  highlighted = false,
  highlightedItems = [],
}) {
  // Guard against out-of-range selectedIndex
  const safeSelectedIndex = selectedIndex >= 0 && selectedIndex < menuItems.length ? selectedIndex : -1;
  const displayValue = safeSelectedIndex >= 0 ? menuItems[safeSelectedIndex] : { title: 'Select' };
  const [searchTerm, setSearchTerm] = React.useState('');
  const [open, setOpen] = React.useState(false);

  const handleMenuSelect = (menuItem) => {
    setSearchTerm('');
    setOpen(false);
    if (menuItem.link) {
      window.location.href = menuItem.link;
      window.location.reload();
      return;
    }
    if (menuItem.onClick) {
      menuItem.onClick(menuItem);
      return;
    }
    if (onItemSelected) {
      onItemSelected(menuItem);
    }
  };

  const groupedMenuItems = group
    ? groupItemsByFirstLetter(menuItems)
    : { all: menuItems };

  const filteredGroupedMenuItems = group
    ? Object.keys(groupedMenuItems).reduce((result, key) => {
        const filtered = groupedMenuItems[key].filter((item) =>
          item.title.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        if (filtered.length > 0) {
          result[key] = filtered;
        }
        return result;
      }, {})
    : { all: menuItems.filter((item) =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()),
      ) };

  return (
    <FormControl
      fullWidth={fullWidth}
      sx={!fullWidth ? { minWidth: width } : {}}
    >
      <Select
        IconComponent={DropdownIcon}
        style={{
          border: !independent ? '1px solid #b2aeb7' : 'none',
          backgroundColor: 'white',
          borderRadius: borderRadius != null ? `${borderRadius}px` : '12px',
          fontSize: '18px !important',
          fontFamily: 'IBM Plex Sans !important',
          fontWeight: '400 !important',
        }}
        MenuProps={{
          ...(menuZIndex ? { style: { zIndex: menuZIndex } } : {}),
          PaperProps: {
            component: WhitePaper,
            sx: {
              padding: '5px',
              backgroundColor: '#FFFFFF !important',
              background: '#FFFFFF !important',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              borderRadius: '8px',
              '& .MuiList-root': {
                backgroundColor: '#FFFFFF !important',
                background: '#FFFFFF !important',
                padding: 0,
              },
              '& .MuiMenuItem-root': {
                backgroundColor: '#FFFFFF !important',
                background: '#FFFFFF !important',
                '&:hover': {
                  backgroundColor: '#F3F4F6 !important',
                },
                '&.Mui-selected': {
                  backgroundColor: '#EDE9FE !important',
                },
              },
            },
          },
          MenuListProps: {
            sx: {
              backgroundColor: '#FFFFFF !important',
              background: '#FFFFFF !important',
              padding: 0,
            },
          },
          fontWeight: '400',
          fontSize: '18px',
        }}
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        value={displayValue?.id ?? displayValue?.value ?? ''}
        displayEmpty
        input={<Input disableUnderline={true} />}
        renderValue={() => (
          <AvatarContainer>
            {showAvatars && displayValue?.title && (
              <Avatar
                title={false}
                size={25}
                variant="beam"
                name={displayValue.title}
                square={false}
              />
            )}
            {showImage && createImageFrame(displayValue)}
            <span
              style={{
                fontSize: '18px !important',
                fontFamily: 'IBM Plex Sans !important',
                fontWeight: '400 !important',
              }}
            >
              {getDisplayText(displayValue?.displayTitle ?? displayValue?.title ?? displayValue?.label ?? displayValue?.name ?? displayValue)}
            </span>
          </AvatarContainer>
        )}
      >
        {search && (
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search...."
            disableUnderline={true}
            onClickCapture={(e) => {
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            sx={{
              border: '1px solid var(--light-mode-purple)',
              padding: '5px',
              borderRadius: '10px',
              '&:hover': {
                outline: 'none',
              },
              marginBottom: '5px',
            }}
          />
        )}
        {Object.keys(filteredGroupedMenuItems).flatMap((groupKey) => {
          const items = filteredGroupedMenuItems[groupKey];
          const elements = [];

          // Add group header if grouping is enabled
          if (group && items.length > 0) {
            elements.push(
              <MenuItem key={`header-${groupKey}`} disabled>
                {groupKey}
              </MenuItem>
            );
          }

          // Add menu items for this group
          items.forEach((menuItem) => {
            const isHighlighted = highlightedItems.includes(menuItem.id);
            elements.push(
              <MenuItem
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontWeight: '400',
                  fontSize: '18px',
                  fontFamily: 'IBM Plex Sans',
                  backgroundColor: isHighlighted ? 'rgba(226, 208, 255, 0.25)' : '#FFFFFF',
                  background: isHighlighted ? 'rgba(226, 208, 255, 0.25)' : '#FFFFFF',
                }}
                key={menuItem.id}
                value={menuItem.id}
                onClick={() => handleMenuSelect(menuItem)}
              >
                {showAvatars && (
                  <Avatar
                    title={false}
                    size={25}
                    variant="beam"
                    name={menuItem.title}
                    square={false}
                  />
                )}
                {showImage && createImageFrame(menuItem)}
                {menuItem.displayTitle ?? menuItem.title}
              </MenuItem>
            );
          });

          return elements;
        })}
      </Select>
    </FormControl>
  );
}

const groupItemsByFirstLetter = (items) => {
  return items.reduce((groupedItems, currentItem) => {
    let groupKey;

    if (/^[0-9]/.test(currentItem.title)) {
      groupKey = '#';
    } else {
      groupKey = currentItem.title.charAt(0).toUpperCase();
    }

    if (!groupedItems[groupKey]) {
      groupedItems[groupKey] = [];
    }

    groupedItems[groupKey].push(currentItem);
    return groupedItems;
  }, {});
};

function createImageFrame(selectedItem) {
  if (selectedItem?.image) {
    return <Frame12841 src={selectedItem.image} alt="Frame 1284" />;
  }
}
